import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { MessagesManagementService } from '../../services/messagesManagementService';
import './MessageSearch.css';
import './MessageAccessibility.css';

interface MessageSearchProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  resultCount: number;
  loading?: boolean;
}

/**
 * Optimized MessageSearch component with debounced search input and memoized suggestions
 * Requirements: 5.1, 5.2, 5.3, 5.4 - Performance optimizations
 */
export const MessageSearch: React.FC<MessageSearchProps> = React.memo(({
  searchQuery,
  onSearchChange,
  resultCount,
  loading = false
}) => {
  const { t } = useLanguage();
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastQueryRef = useRef<string>('');

  // Update local query when prop changes
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  // Optimized debounced search effect with cleanup
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Only trigger search if query actually changed
    if (localQuery !== lastQueryRef.current) {
      debounceTimeoutRef.current = setTimeout(() => {
        onSearchChange(localQuery);
        lastQueryRef.current = localQuery;
      }, 300);
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [localQuery, onSearchChange]);

  // Memoized suggestions to avoid unnecessary recalculations
  const memoizedSuggestions = useMemo(() => {
    try {
      return MessagesManagementService.getSearchSuggestions(localQuery);
    } catch (error) {
      console.error('Error getting search suggestions:', error);
      return [];
    }
  }, [localQuery]);

  // Update suggestions when memoized suggestions change
  useEffect(() => {
    setSuggestions(memoizedSuggestions);
    setSelectedSuggestionIndex(-1);
  }, [memoizedSuggestions]);

  // Optimized input change handler with useCallback
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalQuery(value);
    setShowSuggestions(true);
  }, []);

  // Optimized input focus handler with useCallback
  const handleInputFocus = useCallback(() => {
    setShowSuggestions(true);
    // Suggestions will be updated via memoizedSuggestions effect
  }, []);

  // Optimized input blur handler with useCallback
  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }, 150);
  }, []);

  // Optimized keyboard navigation handler with useCallback
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          handleSuggestionClick(suggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        searchInputRef.current?.blur();
        break;
    }
  }, [showSuggestions, suggestions, selectedSuggestionIndex]);

  // Optimized suggestion click handler with useCallback
  const handleSuggestionClick = useCallback((suggestion: string) => {
    setLocalQuery(suggestion);
    onSearchChange(suggestion);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    searchInputRef.current?.focus();
  }, [onSearchChange]);

  // Optimized clear search handler with useCallback
  const handleClearSearch = useCallback(() => {
    setLocalQuery('');
    onSearchChange('');
    setShowSuggestions(false);
    searchInputRef.current?.focus();
  }, [onSearchChange]);

  // Optimized clear history handler with useCallback
  const handleClearHistory = useCallback(() => {
    try {
      MessagesManagementService.clearSearchHistory();
      // Suggestions will be updated via memoizedSuggestions effect
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  }, []);

  return (
    <div className="message-search">
      <div className="search-input-container">
        <div className="search-input-wrapper">
          <i aria-hidden="true"></i>
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder={t('messagesPage.searchPlaceholder')}
            value={localQuery}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            aria-label={t('messagesPage.searchPlaceholder')}
            aria-describedby="search-results-count"
            aria-expanded={showSuggestions}
            aria-autocomplete="list"
            role="combobox"
          />
          {loading && (
            <div className="search-loading" aria-label={t('messagesPage.searching')}>
              <i className="bi bi-arrow-clockwise spin" aria-hidden="true"></i>
            </div>
          )}
          {localQuery && !loading && (
            <button
              className="clear-search-btn"
              onClick={handleClearSearch}
              aria-label={t('messagesPage.clearSearch')}
              type="button"
            >
              <i className="bi bi-x" aria-hidden="true"></i>
            </button>
          )}
        </div>

        {/* Search Suggestions */}
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="search-suggestions"
            role="listbox"
            aria-label={t('messagesPage.searchSuggestions')}
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion}
                className={`suggestion-item ${index === selectedSuggestionIndex ? 'selected' : ''
                  }`}
                onClick={() => handleSuggestionClick(suggestion)}
                role="option"
                aria-selected={index === selectedSuggestionIndex}
              >
                <i className="bi bi-clock-history suggestion-icon" aria-hidden="true"></i>
                <span className="suggestion-text">{suggestion}</span>
              </div>
            ))}

            {/* Clear history option */}
            {MessagesManagementService.getSearchHistory().length > 0 && (
              <div className="suggestion-actions">
                <button
                  className="clear-history-btn"
                  onClick={handleClearHistory}
                  type="button"
                >
                  <i className="bi bi-trash3" aria-hidden="true"></i>
                  {t('messagesPage.clearHistory')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search Results Count */}
      {searchQuery && (
        <div
          id="search-results-count"
          className="search-results-info"
          role="status"
          aria-live="polite"
        >
          {loading ? (
            <span>{t('messagesPage.searchingText')}</span>
          ) : (
            <span>
              {resultCount === 0
                ? t('messagesPage.notFound')
                : t('messagesPage.foundCount', { count: resultCount, plural: resultCount === 1 ? '' : 's' })
              }
              {searchQuery && (
                <span className="search-query">
                  {' '}{t('messagesPage.for')} "<strong>{searchQuery}</strong>"
                </span>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
});

MessageSearch.displayName = 'MessageSearch';

export default MessageSearch;